import { useState, useEffect, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * MediaCarousel — full-image swipe carousel.
 * Used for both "swipe" (before/after) and "single" (multi-image) gallery formats.
 * Pass `labels` (e.g. ["Before","After"]) to show pill labels on each slide.
 */
export default function MediaCarousel({ images = [], labels = [], format = "single" }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = () => emblaApi && emblaApi.scrollPrev();
  const scrollNext = () => emblaApi && emblaApi.scrollNext();
  const scrollTo = (i) => emblaApi && emblaApi.scrollTo(i);

  if (!images.length) {
    return (
      <div className="w-full aspect-[4/3] md:aspect-[16/10] rounded-2xl md:rounded-3xl bg-slate-100 grid place-items-center text-slate-400 text-sm">
        No images
      </div>
    );
  }

  return (
    <div className="relative" data-testid="media-carousel">
      <div
        className="overflow-hidden rounded-2xl md:rounded-3xl bg-slate-200 select-none"
        ref={emblaRef}
      >
        <div className="flex touch-pan-y">
          {images.map((src, i) => (
            <div
              key={i}
              className="relative flex-[0_0_100%] aspect-[4/3] md:aspect-[16/10]"
              data-testid={`carousel-slide-${i}`}
            >
              <img
                src={src}
                alt={labels[i] || `Slide ${i + 1}`}
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
              {labels[i] && format === "swipe" && (
                <span
                  className={`absolute top-4 left-4 text-white text-xs font-semibold tracking-[0.2em] uppercase px-3 py-1.5 rounded-full ${
                    i === 0 ? "bg-black/60" : "bg-[#00B4D8]"
                  }`}
                >
                  {labels[i]}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous"
            data-testid="carousel-prev"
            onClick={scrollPrev}
            disabled={!canPrev}
            className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-[#0F3A5A] grid place-items-center shadow-xl disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2.2} />
          </button>
          <button
            type="button"
            aria-label="Next"
            data-testid="carousel-next"
            onClick={scrollNext}
            disabled={!canNext}
            className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/95 hover:bg-white text-[#0F3A5A] grid place-items-center shadow-xl disabled:opacity-30 disabled:cursor-not-allowed transition"
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2.2} />
          </button>

          {/* Dots */}
          <div
            className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
            data-testid="carousel-dots"
          >
            {images.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => scrollTo(i)}
                data-testid={`carousel-dot-${i}`}
                className={`h-2 rounded-full transition-all ${
                  i === selectedIndex
                    ? "w-8 bg-white"
                    : "w-2 bg-white/60 hover:bg-white/80"
                }`}
              />
            ))}
          </div>

          {/* Counter */}
          <div
            className="absolute top-4 right-4 bg-black/55 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full"
            data-testid="carousel-counter"
          >
            {selectedIndex + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
}
